import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  FileDown,
  FileSpreadsheet,
  FileText,
  Filter,
  LayoutGrid,
  Play,
  Plus,
  Save,
  Sigma,
  Table as TableIcon,
  Trash2,
} from "lucide-react";

import { pageTransition } from "@/lib/utils";
import { useToast } from "@/hooks/useToast";
import {
  createSavedReport,
  downloadBlob,
  exportCustomReport,
  getDatasetCatalog,
  runCustomReport,
} from "@/api/reports";
import {
  DATASET_LABELS,
  OPERATOR_LABELS,
  type DatasetCatalogEntry,
  type ReportAggregate,
  type ReportAggregateFn,
  type ReportDataset,
  type ReportExportFormat,
  type ReportFilter,
  type ReportOperator,
  type ReportRunResult,
  type ReportSpec,
} from "@/types/report";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { Skeleton, SkeletonCard } from "@/components/ui/Skeleton";

type FilterDraft = {
  id: string;
  field: string;
  op: ReportOperator;
  value: string;
};

type AggregateDraft = {
  id: string;
  fn: ReportAggregateFn;
  field: string; // "*" for count-all
  as: string;
};

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

function toSpec(
  columns: string[],
  filters: FilterDraft[],
  aggregates: AggregateDraft[],
  groupBy: string[],
  sortField: string,
  sortDir: "asc" | "desc",
  limit: string,
): ReportSpec {
  const spec: ReportSpec = {};
  if (columns.length) spec.columns = columns;
  if (filters.length) {
    spec.filters = filters.map((f) => {
      const base: ReportFilter = { field: f.field, op: f.op };
      if (f.op === "is_null" || f.op === "is_not_null") return base;
      if (f.op === "in") {
        base.value = f.value
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      } else {
        base.value = f.value;
      }
      return base;
    });
  }
  if (aggregates.length) {
    spec.aggregates = aggregates.map(
      (a): ReportAggregate => ({
        fn: a.fn,
        field: a.field === "*" ? null : a.field,
        ...(a.as ? { as: a.as } : {}),
      }),
    );
  }
  if (groupBy.length) spec.group_by = groupBy;
  if (sortField) spec.sort = [{ field: sortField, dir: sortDir }];
  const parsed = parseInt(limit, 10);
  if (parsed && parsed > 0) spec.limit = parsed;
  return spec;
}

export function ReportBuilderPage() {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [catalog, setCatalog] = useState<DatasetCatalogEntry[]>([]);
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(true);

  const [dataset, setDataset] = useState<ReportDataset>("EMPLOYEES");
  const [columns, setColumns] = useState<string[]>([]);
  const [filters, setFilters] = useState<FilterDraft[]>([]);
  const [aggregates, setAggregates] = useState<AggregateDraft[]>([]);
  const [groupBy, setGroupBy] = useState<string[]>([]);
  const [sortField, setSortField] = useState<string>("");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [limit, setLimit] = useState<string>("500");

  const [result, setResult] = useState<ReportRunResult | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const [showSave, setShowSave] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveDescription, setSaveDescription] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Load catalog once
  useEffect(() => {
    (async () => {
      try {
        const c = await getDatasetCatalog();
        setCatalog(c);
      } catch {
        addToast("error", "Failed to load report catalog");
      } finally {
        setIsLoadingCatalog(false);
      }
    })();
  }, [addToast]);

  // When dataset changes, reset to that dataset's defaults.
  const currentCatalog = useMemo(
    () => catalog.find((d) => d.dataset === dataset),
    [catalog, dataset],
  );

  useEffect(() => {
    if (!currentCatalog) return;
    setColumns(currentCatalog.default_columns);
    setFilters([]);
    setAggregates([]);
    setGroupBy([]);
    setSortField("");
    setResult(null);
  }, [currentCatalog]);

  const fieldOptions = useMemo(
    () =>
      currentCatalog?.fields.map((f) => ({ value: f.name, label: f.name })) ??
      [],
    [currentCatalog],
  );

  const operatorOptions = useMemo(
    () =>
      currentCatalog?.operators.map((op) => ({
        value: op,
        label: OPERATOR_LABELS[op],
      })) ?? [],
    [currentCatalog],
  );

  const aggregateOptions = useMemo(
    () =>
      currentCatalog?.aggregates.map((fn) => ({
        value: fn,
        label: fn.toUpperCase(),
      })) ?? [],
    [currentCatalog],
  );

  const spec = useMemo(
    () => toSpec(columns, filters, aggregates, groupBy, sortField, sortDir, limit),
    [columns, filters, aggregates, groupBy, sortField, sortDir, limit],
  );

  const handleRun = useCallback(async () => {
    setIsRunning(true);
    try {
      const res = await runCustomReport(dataset, spec);
      setResult(res);
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? "Failed to run report";
      addToast("error", detail);
    } finally {
      setIsRunning(false);
    }
  }, [dataset, spec, addToast]);

  const handleExport = useCallback(
    async (fmt: ReportExportFormat) => {
      setIsExporting(true);
      try {
        const { blob, filename } = await exportCustomReport(
          dataset,
          spec,
          fmt,
          DATASET_LABELS[dataset],
        );
        downloadBlob(blob, filename);
        addToast("success", `Exported as ${fmt}`);
      } catch (err) {
        const detail =
          (err as { response?: { data?: { detail?: string } } })?.response?.data
            ?.detail ?? "Failed to export";
        addToast("error", detail);
      } finally {
        setIsExporting(false);
      }
    },
    [dataset, spec, addToast],
  );

  const handleSave = useCallback(async () => {
    if (!saveName.trim()) {
      addToast("error", "Name is required");
      return;
    }
    setIsSaving(true);
    try {
      await createSavedReport({
        name: saveName.trim(),
        description: saveDescription.trim() || undefined,
        dataset,
        spec,
      });
      addToast("success", "Report saved");
      setShowSave(false);
      setSaveName("");
      setSaveDescription("");
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? "Failed to save report";
      addToast("error", detail);
    } finally {
      setIsSaving(false);
    }
  }, [saveName, saveDescription, dataset, spec, addToast]);

  const toggleColumn = (name: string) => {
    setColumns((prev) =>
      prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name],
    );
  };

  const addFilter = () => {
    if (!currentCatalog) return;
    setFilters((prev) => [
      ...prev,
      {
        id: uid(),
        field: currentCatalog.fields[0]?.name ?? "",
        op: "eq",
        value: "",
      },
    ]);
  };

  const addAggregate = () => {
    if (!currentCatalog) return;
    setAggregates((prev) => [
      ...prev,
      { id: uid(), fn: "count", field: "*", as: "" },
    ]);
  };

  const toggleGroupBy = (name: string) => {
    setGroupBy((prev) =>
      prev.includes(name) ? prev.filter((g) => g !== name) : [...prev, name],
    );
  };

  if (isLoadingCatalog) {
    // Shape the skeleton like the real builder: narrow sidebar for the
    // dataset picker + tall canvas for field selection. A single
    // full-width block made the layout collapse dramatically once the
    // catalog loaded.
    return (
      <motion.div {...pageTransition}>
        <PageHeader title="Report builder" />
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-1 space-y-3">
            <Skeleton className="h-8 w-full" />
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </div>
          <div className="lg:col-span-3">
            <SkeletonCard />
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div {...pageTransition}>
      <div className="flex items-center gap-2 mb-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/reports")}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to reports
        </Button>
      </div>
      <PageHeader
        title="Custom report builder"
        subtitle="Pick a dataset, shape the query, preview, then save or export."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => setShowSave(true)}
              disabled={!columns.length && !aggregates.length}
            >
              <Save className="h-4 w-4" />
              Save
            </Button>
            <Button onClick={handleRun} isLoading={isRunning}>
              <Play className="h-4 w-4" />
              Run
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: builder form */}
        <div className="lg:col-span-1 space-y-4">
          {/* Dataset picker */}
          <Card>
            <div className="flex items-center gap-2 mb-3">
              <LayoutGrid className="h-4 w-4 text-accent" />
              <h3 className="font-semibold font-heading text-text-primary">
                Dataset
              </h3>
            </div>
            <Select
              value={dataset}
              onChange={(e) => setDataset(e.target.value as ReportDataset)}
              options={catalog.map((c) => ({
                value: c.dataset,
                label: DATASET_LABELS[c.dataset],
              }))}
            />
          </Card>

          {/* Columns */}
          <Card>
            <div className="flex items-center gap-2 mb-3">
              <TableIcon className="h-4 w-4 text-accent" />
              <h3 className="font-semibold font-heading text-text-primary">
                Columns
              </h3>
            </div>
            <p className="text-xs text-text-muted mb-3">
              Only used in projection mode (ignored if you add aggregates).
            </p>
            <div className="flex flex-wrap gap-1.5 max-h-56 overflow-auto">
              {currentCatalog?.fields.map((f) => {
                const active = columns.includes(f.name);
                return (
                  <button
                    key={f.name}
                    onClick={() => toggleColumn(f.name)}
                    className={
                      active
                        ? "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent border border-accent/30"
                        : "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-background text-text-muted border border-border hover:border-accent/30"
                    }
                  >
                    {f.name}
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Filters */}
          <Card>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-accent" />
                <h3 className="font-semibold font-heading text-text-primary">
                  Filters
                </h3>
              </div>
              <Button size="sm" variant="ghost" onClick={addFilter}>
                <Plus className="h-3 w-3" />
                Add
              </Button>
            </div>
            {filters.length === 0 ? (
              <p className="text-xs text-text-muted">No filters yet.</p>
            ) : (
              <div className="space-y-2">
                {filters.map((f, idx) => (
                  <div
                    key={f.id}
                    className="flex items-start gap-1.5 rounded-lg border border-border p-2 bg-background"
                  >
                    <div className="grid grid-cols-3 gap-1 flex-1">
                      <Select
                        value={f.field}
                        onChange={(e) => {
                          const v = e.target.value;
                          setFilters((prev) =>
                            prev.map((x, i) =>
                              i === idx ? { ...x, field: v } : x,
                            ),
                          );
                        }}
                        options={fieldOptions}
                      />
                      <Select
                        value={f.op}
                        onChange={(e) => {
                          const v = e.target.value as ReportOperator;
                          setFilters((prev) =>
                            prev.map((x, i) =>
                              i === idx ? { ...x, op: v } : x,
                            ),
                          );
                        }}
                        options={operatorOptions}
                      />
                      {f.op !== "is_null" && f.op !== "is_not_null" ? (
                        <Input
                          value={f.value}
                          placeholder={f.op === "in" ? "a, b, c" : "value"}
                          onChange={(e) => {
                            const v = e.target.value;
                            setFilters((prev) =>
                              prev.map((x, i) =>
                                i === idx ? { ...x, value: v } : x,
                              ),
                            );
                          }}
                        />
                      ) : (
                        <div />
                      )}
                    </div>
                    <button
                      onClick={() =>
                        setFilters((prev) => prev.filter((x) => x.id !== f.id))
                      }
                      className="p-1.5 text-text-muted hover:text-danger"
                      aria-label="Remove filter"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Aggregates + group-by */}
          <Card>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sigma className="h-4 w-4 text-accent" />
                <h3 className="font-semibold font-heading text-text-primary">
                  Aggregate / group by
                </h3>
              </div>
              <Button size="sm" variant="ghost" onClick={addAggregate}>
                <Plus className="h-3 w-3" />
                Add
              </Button>
            </div>

            {aggregates.length === 0 ? (
              <p className="text-xs text-text-muted mb-3">
                No aggregates — the builder runs in projection mode.
              </p>
            ) : (
              <div className="space-y-2 mb-3">
                {aggregates.map((a, idx) => (
                  <div
                    key={a.id}
                    className="flex items-start gap-1.5 rounded-lg border border-border p-2 bg-background"
                  >
                    <div className="grid grid-cols-3 gap-1 flex-1">
                      <Select
                        value={a.fn}
                        onChange={(e) => {
                          const v = e.target.value as ReportAggregateFn;
                          setAggregates((prev) =>
                            prev.map((x, i) =>
                              i === idx ? { ...x, fn: v } : x,
                            ),
                          );
                        }}
                        options={aggregateOptions}
                      />
                      <Select
                        value={a.field}
                        onChange={(e) => {
                          const v = e.target.value;
                          setAggregates((prev) =>
                            prev.map((x, i) =>
                              i === idx ? { ...x, field: v } : x,
                            ),
                          );
                        }}
                        options={[{ value: "*", label: "*" }, ...fieldOptions]}
                      />
                      <Input
                        value={a.as}
                        placeholder="alias"
                        onChange={(e) => {
                          const v = e.target.value;
                          setAggregates((prev) =>
                            prev.map((x, i) =>
                              i === idx ? { ...x, as: v } : x,
                            ),
                          );
                        }}
                      />
                    </div>
                    <button
                      onClick={() =>
                        setAggregates((prev) =>
                          prev.filter((x) => x.id !== a.id),
                        )
                      }
                      className="p-1.5 text-text-muted hover:text-danger"
                      aria-label="Remove aggregate"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <p className="text-xs font-medium text-text-muted uppercase tracking-wider mb-2">
              Group by
            </p>
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-auto">
              {currentCatalog?.fields.map((f) => {
                const active = groupBy.includes(f.name);
                return (
                  <button
                    key={f.name}
                    onClick={() => toggleGroupBy(f.name)}
                    className={
                      active
                        ? "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent border border-accent/30"
                        : "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-background text-text-muted border border-border hover:border-accent/30"
                    }
                  >
                    {f.name}
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Sort + limit */}
          <Card>
            <h3 className="font-semibold font-heading text-text-primary mb-3">
              Sort &amp; limit
            </h3>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className="col-span-2">
                <Select
                  label="Sort by"
                  value={sortField}
                  onChange={(e) => setSortField(e.target.value)}
                  options={[{ value: "", label: "—" }, ...fieldOptions]}
                />
              </div>
              <Select
                label="Dir"
                value={sortDir}
                onChange={(e) =>
                  setSortDir(e.target.value as "asc" | "desc")
                }
                options={[
                  { value: "asc", label: "Asc" },
                  { value: "desc", label: "Desc" },
                ]}
              />
            </div>
            <Input
              label="Row limit"
              type="number"
              min={1}
              max={5000}
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
            />
          </Card>
        </div>

        {/* Right: preview + export */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-semibold font-heading text-text-primary">
                  Preview
                </h3>
                <p className="text-xs text-text-muted">
                  {result
                    ? `${result.row_count} row${result.row_count === 1 ? "" : "s"} · limit ${result.limit_applied}`
                    : "Run the report to see results."}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => handleExport("CSV")}
                  isLoading={isExporting}
                  disabled={!result}
                >
                  <FileDown className="h-3.5 w-3.5" />
                  CSV
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => handleExport("XLSX")}
                  isLoading={isExporting}
                  disabled={!result}
                >
                  <FileSpreadsheet className="h-3.5 w-3.5" />
                  Excel
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => handleExport("PDF")}
                  isLoading={isExporting}
                  disabled={!result}
                >
                  <FileText className="h-3.5 w-3.5" />
                  PDF
                </Button>
              </div>
            </div>
            {result ? (
              <div className="overflow-auto max-h-[540px] border border-border rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-background sticky top-0 z-10">
                    <tr>
                      {result.columns.map((c) => (
                        <th
                          key={c}
                          className="text-left px-3 py-2 font-medium text-text-muted text-xs uppercase tracking-wider whitespace-nowrap"
                        >
                          {c}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.map((row, i) => (
                      <tr
                        key={i}
                        className="border-t border-border hover:bg-background/50"
                      >
                        {row.map((v, j) => (
                          <td
                            key={j}
                            className="px-3 py-2 text-text-primary whitespace-nowrap"
                          >
                            {v === null ? (
                              <span className="text-text-muted">—</span>
                            ) : (
                              String(v)
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {result.rows.length === 0 && (
                  <p className="text-center text-text-muted py-8 text-sm">
                    No rows matched.
                  </p>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-24 text-text-muted gap-2">
                <Download className="h-8 w-8" />
                <p className="text-sm">
                  Click <span className="text-text-primary">Run</span> to
                  preview the report.
                </p>
              </div>
            )}
          </Card>

          {/* Spec preview for debugging */}
          {result && (
            <Card>
              <h3 className="font-semibold font-heading text-text-primary mb-2 flex items-center gap-2">
                Spec
                <Badge variant="default">{dataset}</Badge>
              </h3>
              <pre className="text-xs bg-background rounded-lg p-3 overflow-auto text-text-muted">
                {JSON.stringify(spec, null, 2)}
              </pre>
            </Card>
          )}
        </div>
      </div>

      {/* Save modal */}
      <Modal
        isOpen={showSave}
        onClose={() => setShowSave(false)}
        title="Save report"
        size="md"
      >
        <div className="space-y-4">
          <Input
            label="Name"
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            placeholder="e.g. Monthly headcount"
          />
          <Input
            label="Description (optional)"
            value={saveDescription}
            onChange={(e) => setSaveDescription(e.target.value)}
          />
          <p className="text-xs text-text-muted">
            Saved reports can be run on demand or put on a schedule with email
            delivery from the <span className="text-text-primary">Saved</span>{" "}
            tab.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowSave(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} isLoading={isSaving}>
              Save
            </Button>
          </div>
        </div>
      </Modal>
    </motion.div>
  );
}
